import { useEffect, useState, useCallback, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Typography, Button, Tooltip, Alert, Input, Space, Modal, Steps, Table, Tag, Pagination } from 'antd';
import { Sk, SkStyleTag } from '../../shared/components/TableSkeleton';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  SubnodeOutlined, HolderOutlined, CheckOutlined, CloseOutlined,
  FolderAddOutlined, CaretRightOutlined, CaretDownOutlined,
  UploadOutlined, DownloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';

const { Text } = Typography;

const buildTree = (cats) => {
  const map = {};
  cats.forEach(c => { map[c.id] = { ...c, children: [] }; });
  const roots = [];
  cats.forEach(c => {
    if (c.id_parent && map[c.id_parent]) map[c.id_parent].children.push(map[c.id]);
    else roots.push(map[c.id]);
  });
  const sort = (nodes) => nodes.sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre));
  const sortTree = (nodes) => { sort(nodes); nodes.forEach(n => sortTree(n.children)); return nodes; };
  return sortTree(roots);
};

const flattenTree = (nodes, depth = 0) => {
  const result = [];
  nodes.forEach(n => {
    result.push({ ...n, depth });
    if (n.children?.length) result.push(...flattenTree(n.children, depth + 1));
  });
  return result;
};

const CircleBtn = ({ icon, onClick, danger, title, disabled }) => (
  <Tooltip title={title}>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 32, height: 32,
        border: `1px solid ${danger ? '#fca5a5' : '#e5e7eb'}`,
        borderRadius: '50%',
        background: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: danger ? '#ef4444' : '#374151',
        opacity: disabled ? 0.45 : 1,
        transition: 'border-color 0.15s',
        fontSize: 13, flexShrink: 0,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = danger ? '#ef4444' : '#374151'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = danger ? '#fca5a5' : '#e5e7eb'; }}
    >
      {icon}
    </button>
  </Tooltip>
);

export const AdminCategorias = () => {
  const { theme } = useClient();
  const primary = theme?.primaryColor || '#0f172a';
  const primaryText = theme?.primaryTextColor || '#fff';
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingId, setSavingId] = useState(null);

  // Drafts: { tempId, nombre, id_parent }
  const [drafts, setDrafts] = useState([]);

  const [confirmDelId, setConfirmDelId] = useState(null);
  const [collapsedIds, setCollapsedIds] = useState(new Set());

  // paginación
  const [catPage, setCatPage] = useState(1);
  const [catPageSize, setCatPageSize] = useState(25);

  // importar
  const [importModal, setImportModal] = useState(false);
  const [importStep, setImportStep] = useState(0);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const catFileInputRef = useRef(null);

  const toggleCollapse = (id) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Drag state
  const dragId = useRef(null);
  const [dragSrcId, setDragSrcId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.catalogo.getCategorias();
      if (res.status === 'success') setCategorias(res.data || []);
      else setError(res.message || 'Error al cargar categorías');
    } catch {
      setError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // --- Edit inline ---
  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditingValue(cat.nombre);
  };

  const cancelEdit = () => { setEditingId(null); setEditingValue(''); };

  const saveEdit = async (cat) => {
    const nombre = editingValue.trim();
    if (!nombre) { cancelEdit(); return; }
    setSavingId(cat.id);
    try {
      await api.admin.catalogo.editarCategoria(cat.id, {
        nombre, id_parent: cat.id_parent || null, orden: cat.orden,
      });
      setEditingId(null);
      cargar();
    } finally {
      setSavingId(null);
    }
  };

  // --- Drafts ---
  const addDraft = (id_parent = null) => {
    setEditingId(null);
    setDrafts(d => [...d, { tempId: Date.now(), nombre: '', id_parent }]);
  };

  const updateDraft = (tempId, nombre) =>
    setDrafts(d => d.map(x => x.tempId === tempId ? { ...x, nombre } : x));

  const removeDraft = (tempId) =>
    setDrafts(d => d.filter(x => x.tempId !== tempId));

  const saveDraft = async (draft) => {
    const nombre = draft.nombre.trim();
    if (!nombre) { removeDraft(draft.tempId); return; }
    setSavingId(draft.tempId);
    try {
      const siblings = categorias.filter(c => (c.id_parent ?? null) === (draft.id_parent ?? null));
      const orden = siblings.reduce((max, c) => Math.max(max, c.orden || 0), 0) + 10;
      await api.admin.catalogo.crearCategoria({
        nombre, id_parent: draft.id_parent || null, orden,
      });
      removeDraft(draft.tempId);
      cargar();
    } finally {
      setSavingId(null);
    }
  };

  // --- Delete ---
  const handleEliminar = async (id) => {
    setConfirmDelId(null);
    try {
      await api.admin.catalogo.eliminarCategoria(id);
      cargar();
    } catch {
      setError('Error al desactivar');
    }
  };

  // --- Drag & drop (reorder mismo nivel, optimistic update) ---
  const handleDragStart = (e, cat) => {
    dragId.current = cat.id;
    setDragSrcId(cat.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, cat) => {
    e.preventDefault();
    if (cat.id !== dragId.current) setDragOverId(cat.id);
  };

  const handleDragLeave = () => setDragOverId(null);

  const handleDrop = async (e, targetCat) => {
    e.preventDefault();
    setDragOverId(null);
    setDragSrcId(null);
    const srcId = dragId.current;
    dragId.current = null;
    if (!srcId || srcId === targetCat.id) return;

    const srcCat = categorias.find(c => c.id === srcId);
    if (!srcCat) return;
    if ((srcCat.id_parent ?? null) !== (targetCat.id_parent ?? null)) return;

    const flat = flattenTree(buildTree(categorias.filter(c => c.activo)));
    const siblings = flat.filter(c => (c.id_parent ?? null) === (srcCat.id_parent ?? null));
    const srcIdx = siblings.findIndex(c => c.id === srcId);
    const tgtIdx = siblings.findIndex(c => c.id === targetCat.id);
    if (srcIdx === -1 || tgtIdx === -1) return;

    const reordered = [...siblings];
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(tgtIdx, 0, moved);

    // Optimistic update: reflejar el nuevo orden localmente de inmediato
    const newOrden = {};
    reordered.forEach((cat, idx) => { newOrden[cat.id] = idx * 10; });
    setCategorias(prev => prev.map(c => newOrden[c.id] !== undefined ? { ...c, orden: newOrden[c.id] } : c));

    // Guardar en background, revertir si falla
    try {
      await Promise.all(
        reordered.map((cat, idx) =>
          api.admin.catalogo.editarCategoria(cat.id, {
            nombre: cat.nombre,
            id_parent: cat.id_parent || null,
            orden: idx * 10,
          })
        )
      );
    } catch {
      setError('Error al guardar el orden');
      cargar();
    }
  };

  const handleDragEnd = () => { dragId.current = null; setDragSrcId(null); setDragOverId(null); };

  // --- Importar categorías ---
  const abrirImportCat = () => {
    setImportModal(true); setImportStep(0); setImportFile(null);
    setImportPreview([]); setImportError(null); setImportResult(null);
  };
  const cerrarImportCat = () => { if (importLoading) return; setImportModal(false); };

  const descargarPlantillaCat = async () => {
    try {
      const res = await api.admin.catalogo.descargarPlantillaCategorias();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'plantilla_categorias.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { /* silencioso */ }
  };

  const handleCatFileSelect = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImportFile(file); setImportError(null);
  };

  const handleCatPreview = async () => {
    if (!importFile) return;
    setImportLoading(true); setImportError(null);
    try {
      const res = await api.admin.catalogo.previewImportacionCategorias(importFile);
      if (res.status === 'success') { setImportPreview(res.data || []); setImportStep(1); }
      else setImportError(res.message || 'Error al leer el archivo');
    } catch { setImportError('Error al conectar con el servidor'); }
    finally { setImportLoading(false); }
  };

  const handleCatImportar = async () => {
    setImportLoading(true); setImportError(null);
    try {
      const res = await api.admin.catalogo.importarCategorias(importFile);
      if (res.status === 'success') { setImportResult(res.data); setImportStep(2); cargar(); }
      else setImportError(res.message || 'Error al importar');
    } catch { setImportError('Error al conectar con el servidor'); }
    finally { setImportLoading(false); }
  };

  const catPreviewColumns = [
    { title: 'Fila', dataIndex: 'fila', width: 55 },
    { title: 'Categoría', dataIndex: 'categoria', width: 160, ellipsis: true },
    { title: 'Subcategoría', dataIndex: 'subcategoria', width: 160, ellipsis: true, render: v => v || '—' },
    { title: 'Orden', dataIndex: 'orden', width: 70 },
    {
      title: 'Acción', dataIndex: 'accion', width: 160,
      render: (v, r) => {
        if (r.error) return <Tag color="error" icon={<ExclamationCircleOutlined />}>Error</Tag>;
        if (v === 'ya_existe') return <Tag color="default">Ya existe</Tag>;
        if (v === 'nueva_subcategoria') return <Tag color="blue">Nueva subcategoría</Tag>;
        return <Tag color="success" icon={<CheckCircleOutlined />}>Nueva categoría</Tag>;
      },
    },
    { title: 'Error', dataIndex: 'error', ellipsis: true, render: v => v ? <span style={{ color: '#ef4444', fontSize: 12 }}>{v}</span> : null },
  ];

  const filasNuevas = importPreview.filter(f => !f.error && f.accion !== 'ya_existe');
  const filasExistentes = importPreview.filter(f => f.accion === 'ya_existe');

  // --- Render list: cats + drafts intercalados, respetando colapso ---
  const flatCats = flattenTree(buildTree(categorias.filter(c => c.activo)));
  const hasChildren = (id) => categorias.some(c => c.id_parent === id && c.activo);

  // Filtrar cats cuyo ancestro está colapsado
  const hiddenAncestors = new Set();
  const visibleCats = flatCats.filter(cat => {
    if (cat.id_parent && (collapsedIds.has(cat.id_parent) || hiddenAncestors.has(cat.id_parent))) {
      hiddenAncestors.add(cat.id);
      return false;
    }
    return true;
  });

  const rows = [];
  visibleCats.forEach(cat => {
    rows.push({ type: 'cat', data: cat });
    // Solo mostrar drafts hijos si el padre no está colapsado
    if (!collapsedIds.has(cat.id)) {
      drafts
        .filter(d => d.id_parent === cat.id)
        .forEach(d => rows.push({ type: 'draft', data: { ...d, depth: cat.depth + 1 } }));
    }
  });
  // Drafts raíz al final
  drafts
    .filter(d => d.id_parent === null)
    .forEach(d => rows.push({ type: 'draft', data: { ...d, depth: 0 } }));

  const totalRows = rows.filter(r => r.type === 'cat').length;
  const pageStart = (catPage - 1) * catPageSize;
  const pageEnd = pageStart + catPageSize;

  // Paginamos solo las filas de tipo 'cat'; los drafts siempre son visibles
  let catCount = 0;
  const paginatedRows = rows.filter(row => {
    if (row.type === 'draft') return true;
    catCount++;
    return catCount > pageStart && catCount <= pageEnd;
  });

  const isEmpty = rows.length === 0;

  const handleCatPageChange = (newPage, newSize) => {
    if (newSize !== catPageSize) { setCatPageSize(newSize); setCatPage(1); }
    else setCatPage(newPage);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Categorías</Text>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Tooltip title="Actualizar">
            <Button type="text" icon={<ReloadOutlined />} onClick={cargar} loading={loading} />
          </Tooltip>
          <Button icon={<UploadOutlined />} onClick={abrirImportCat}>
            Importar
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => addDraft(null)}
            style={{ background: primary, borderColor: primary, color: primaryText }}
          >
            Crear categoría
          </Button>
        </div>
      </div>

      <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 20 }}>
        Para organizar tus productos, creá categorías y subcategorías que aparecerán como filtros en el catálogo.
      </Text>

      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} closable onClose={() => setError(null)} />
      )}

      {loading ? (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <SkStyleTag />
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '11px 16px', minHeight: 52,
              borderBottom: i === 7 ? 'none' : '1px solid #f3f4f6',
            }}>
              <Sk w={15} h={15} r={4} />
              <Sk w={`${30 + (i % 4) * 8}%`} h={13} />
              <div style={{ flex: 1 }} />
              <Sk w={28} h={28} r={'50%'} />
              <Sk w={28} h={28} r={'50%'} />
            </div>
          ))}
        </div>
      ) : isEmpty ? (
        <div style={{
          background: '#fff', border: '2px dashed #e5e7eb', borderRadius: 8,
          padding: '40px 0', textAlign: 'center',
        }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Todavía no hay categorías.{' '}
            <span
              style={{ color: primary, cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => addDraft(null)}
            >
              Creá la primera
            </span>.
          </Text>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflowX: 'auto' }}>
          <div style={{ minWidth: 480 }}>
          {paginatedRows.map((row, i) => {
            const isLast = i === paginatedRows.length - 1;

            if (row.type === 'draft') {
              const draft = row.data;
              const isSaving = savingId === draft.tempId;
              return (
                <div
                  key={`draft-${draft.tempId}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '11px 16px',
                    paddingLeft: 12 + draft.depth * 28,
                    borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
                    background: draft.depth > 0 ? '#fafafa' : '#fff',
                    borderTop: '1px dashed #e5e7eb',
                    minHeight: 52,
                  }}
                >
                  <HolderOutlined style={{ color: '#e5e7eb', fontSize: 15, flexShrink: 0 }} />
                  {draft.depth > 0 && (
                    <SubnodeOutlined style={{ fontSize: 13, color: '#d1d5db', flexShrink: 0 }} />
                  )}
                  <Input
                    value={draft.nombre}
                    onChange={(e) => updateDraft(draft.tempId, e.target.value)}
                    onPressEnter={() => saveDraft(draft)}
                    onKeyDown={(e) => e.key === 'Escape' && removeDraft(draft.tempId)}
                    placeholder={draft.depth > 0 ? 'Nombre de subcategoría...' : 'Nombre de categoría...'}
                    autoFocus
                    size="small"
                    disabled={isSaving}
                    style={{ flex: 1, maxWidth: 320 }}
                  />
                  <Space size={6} style={{ flexShrink: 0 }}>
                    <CircleBtn icon={<CheckOutlined />} title="Guardar" onClick={() => saveDraft(draft)} disabled={isSaving} />
                    <CircleBtn icon={<CloseOutlined />} title="Cancelar" onClick={() => removeDraft(draft.tempId)} disabled={isSaving} />
                  </Space>
                </div>
              );
            }

            // type === 'cat'
            const cat = row.data;
            const isEditing = editingId === cat.id;
            const isSaving = savingId === cat.id;
            const showConfirm = confirmDelId === cat.id;
            const isDragOver = dragOverId === cat.id;

            return (
              <div
                key={cat.id}
                draggable={!isEditing}
                onDragStart={(e) => handleDragStart(e, cat)}
                onDragOver={(e) => handleDragOver(e, cat)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, cat)}
                onDragEnd={handleDragEnd}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '11px 16px',
                  paddingLeft: 12 + cat.depth * 28,
                  borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
                  background: isDragOver ? '#eff6ff' : cat.depth > 0 ? '#fafafa' : '#fff',
                  borderTop: isDragOver ? '2px solid #3b82f6' : undefined,
                  minHeight: 52,
                  transition: 'background 0.1s, opacity 0.1s',
                  opacity: dragSrcId === cat.id ? 0.4 : 1,
                }}
              >
                {/* Drag handle */}
                <HolderOutlined
                  style={{ color: '#d1d5db', cursor: isEditing ? 'default' : 'grab', fontSize: 15, flexShrink: 0 }}
                />

                {cat.depth > 0 && (
                  <SubnodeOutlined style={{ fontSize: 13, color: '#d1d5db', flexShrink: 0 }} />
                )}

                {/* Expand/collapse arrow for categories with children */}
                {hasChildren(cat.id) ? (
                  <button
                    type="button"
                    onClick={() => toggleCollapse(cat.id)}
                    style={{
                      background: 'none', border: 'none', padding: '2px 4px',
                      cursor: 'pointer', color: '#9ca3af', flexShrink: 0,
                      display: 'inline-flex', alignItems: 'center', fontSize: 11,
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#374151'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
                  >
                    {collapsedIds.has(cat.id) ? <CaretRightOutlined /> : <CaretDownOutlined />}
                  </button>
                ) : (
                  <span style={{ width: 20, flexShrink: 0 }} />
                )}

                {isEditing ? (
                  <Input
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onPressEnter={() => saveEdit(cat)}
                    onKeyDown={(e) => e.key === 'Escape' && cancelEdit()}
                    autoFocus
                    size="small"
                    disabled={isSaving}
                    style={{ flex: 1, maxWidth: 320 }}
                  />
                ) : (
                  <Text
                    style={{ flex: 1, fontSize: 14, fontWeight: cat.depth === 0 ? 600 : 400, color: primary }}
                    onDoubleClick={() => startEdit(cat)}
                  >
                    {cat.nombre}
                  </Text>
                )}

                {showConfirm && !isEditing && (
                  <Text style={{ fontSize: 12, color: '#6b7280' }}>¿Desactivar?</Text>
                )}

                <Space size={6} style={{ flexShrink: 0 }}>
                  {isEditing ? (
                    <>
                      <CircleBtn icon={<CheckOutlined />} title="Guardar" onClick={() => saveEdit(cat)} disabled={isSaving} />
                      <CircleBtn icon={<CloseOutlined />} title="Cancelar" onClick={cancelEdit} disabled={isSaving} />
                    </>
                  ) : showConfirm ? (
                    <>
                      <CircleBtn icon={<CheckOutlined />} title="Confirmar" danger onClick={() => handleEliminar(cat.id)} />
                      <CircleBtn icon={<CloseOutlined />} title="Cancelar" onClick={() => setConfirmDelId(null)} />
                    </>
                  ) : (
                    <>
                      {cat.depth === 0 && <CircleBtn icon={<FolderAddOutlined />} title="Agregar subcategoría" onClick={() => addDraft(cat.id)} />}
                      <CircleBtn icon={<EditOutlined />} title="Editar nombre" onClick={() => startEdit(cat)} />
                      <CircleBtn icon={<DeleteOutlined />} title="Desactivar" danger onClick={() => setConfirmDelId(cat.id)} />
                    </>
                  )}
                </Space>
              </div>
            );
          })}
          </div>
        </div>
      )}

      {/* Paginación categorías */}
      {!loading && totalRows > catPageSize && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            current={catPage}
            pageSize={catPageSize}
            total={totalRows}
            onChange={handleCatPageChange}
            onShowSizeChange={handleCatPageChange}
            showSizeChanger
            pageSizeOptions={['10', '25', '50']}
            showTotal={(t, [from, to]) => `${from}–${to} de ${t}`}
            size="small"
          />
        </div>
      )}

      {(drafts.length > 0 || editingId) && (
        <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 12, textAlign: 'center' }}>
          Enter para guardar · Escape para cancelar
        </Text>
      )}

      {/* Modal importar categorías */}
      <Modal
        open={importModal}
        title="Importar categorías desde Excel"
        onCancel={cerrarImportCat}
        width={importStep === 1 ? 700 : 500}
        footer={null}
        destroyOnClose
      >
        <Steps
          current={importStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={[{ title: 'Cargar archivo' }, { title: 'Revisar' }, { title: 'Resultado' }]}
        />

        {/* Paso 0: subir */}
        {importStep === 0 && (
          <div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>Plantilla</Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                La plantilla tiene columnas <strong>categoria</strong>, <strong>subcategoria</strong> y <strong>orden</strong>. Podés dejar subcategoria vacía para crear categorías raíz.
              </Text>
              <div style={{ marginTop: 10 }}>
                <Button icon={<DownloadOutlined />} onClick={descargarPlantillaCat} size="small">
                  Descargar plantilla .xlsx
                </Button>
              </div>
            </div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Archivo a importar</Text>
            <input ref={catFileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleCatFileSelect} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <Button icon={<UploadOutlined />} onClick={() => catFileInputRef.current?.click()}>Seleccionar archivo</Button>
              {importFile && <Text type="secondary" style={{ fontSize: 13 }}>{importFile.name}</Text>}
            </div>
            {importError && <Alert type="error" message={importError} style={{ marginBottom: 12 }} />}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={cerrarImportCat}>Cancelar</Button>
              <Button type="primary" disabled={!importFile} loading={importLoading} onClick={handleCatPreview}
                style={{ background: primary, borderColor: primary, color: primaryText }}>
                Siguiente
              </Button>
            </div>
          </div>
        )}

        {/* Paso 1: preview */}
        {importStep === 1 && (
          <div>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Tag color="success">{filasNuevas.length} a crear</Tag>
              {filasExistentes.length > 0 && <Tag color="default">{filasExistentes.length} ya existentes (se omiten)</Tag>}
            </div>
            <Table
              dataSource={importPreview}
              columns={catPreviewColumns}
              rowKey="fila"
              size="small"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              scroll={{ x: 560 }}
            />
            {importError && <Alert type="error" message={importError} style={{ margin: '12px 0' }} />}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Button onClick={() => { setImportStep(0); setImportError(null); }}>Volver</Button>
              <Button type="primary" disabled={filasNuevas.length === 0} loading={importLoading} onClick={handleCatImportar}
                style={{ background: primary, borderColor: primary, color: primaryText }}>
                Crear {filasNuevas.length} categoría{filasNuevas.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Paso 2: resultado */}
        {importStep === 2 && importResult && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 48, color: '#22c55e', marginBottom: 12 }} />
            <Text strong style={{ display: 'block', fontSize: 17, marginBottom: 6 }}>
              {importResult.categorias_creadas} categoría{importResult.categorias_creadas !== 1 ? 's' : ''} y{' '}
              {importResult.subcategorias_creadas} subcategoría{importResult.subcategorias_creadas !== 1 ? 's' : ''} creadas
            </Text>
            <Button type="primary" onClick={cerrarImportCat} style={{ marginTop: 16, background: primary, borderColor: primary, color: primaryText }}>
              Cerrar
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};
